import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';

interface MediaSectionProps {
  isLoading: boolean;
}

const MediaSection = ({ isLoading }: MediaSectionProps) => {
  const { control, setValue } = useFormContext<InvestmentFormData>();
  const [mainImageFile, removeMainImage, mainImageUrl] = useWatch({
    control,
    name: ['mainImageFile', 'removeMainImage', 'mainImageUrl'],
  });
  const objectUrlRef = useRef<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(mainImageUrl ?? null);
  const previewUrl = mainImageFile
    ? localPreviewUrl
    : removeMainImage
      ? null
      : (mainImageUrl ?? null);

  useEffect(() => {
    if (!mainImageFile) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setLocalPreviewUrl(mainImageUrl ?? null);
      return;
    }

    const nextObjectUrl = URL.createObjectURL(mainImageFile);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    objectUrlRef.current = nextObjectUrl;
    setLocalPreviewUrl(nextObjectUrl);

    return () => {
      if (objectUrlRef.current === nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
        objectUrlRef.current = null;
      }
    };
  }, [mainImageFile, mainImageUrl]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setValue('mainImageFile', file, { shouldDirty: true });
    setValue('removeMainImage', false, { shouldDirty: true });
    event.target.value = '';
  };

  const handleRemoveImage = () => {
    setValue('mainImageFile', null, { shouldDirty: true });
    setValue('removeMainImage', true, { shouldDirty: true });
    setLocalPreviewUrl(null);
  };

  return (
    <FormSection title="7. Imagen" description="Mantiene la subida opcional de imagen principal.">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      >
        <Box
          sx={{
            width: 220,
            height: 160,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Vista previa de la imagen de la inversión"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
              No se ha seleccionado ninguna imagen
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="contained" component="label" disabled={isLoading}>
            Subir nueva imagen
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>
          {previewUrl ? (
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveImage}
              disabled={isLoading}
            >
              Eliminar imagen
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </FormSection>
  );
};

export default MediaSection;
